import type React from 'react';
import { useEffect, useState } from 'react';
import { useEditMode } from '@/contexts/EditModeContext';
import i18n from '@/i18n';

// Helper to flatten the store
function flatten(obj: any, prefix = ''): Record<string, any> {
  const out: Record<string, any> = {};
  if (!obj || typeof obj !== 'object') return out;
  for (const k of Object.keys(obj)) {
    const v = obj[k];
    const pk = prefix ? `${prefix}.${k}` : k;
    if (v && typeof v === 'object' && !Array.isArray(v)) {
      Object.assign(out, flatten(v, pk));
    } else {
      out[pk] = v;
    }
  }
  return out;
}

export const ClickToEditListener: React.FC = () => {
  const { isEditMode, canEdit, setEditTarget, editTarget } = useEditMode();
  const [reverseMap, setReverseMap] = useState<Map<string, Array<{ ns: string; key: string }>> | null>(null);
  
  // Build reverse map when edit mode is activated
  useEffect(() => {
    if (!isEditMode || !canEdit) {
      setReverseMap(null);
      return;
    }

    const buildMap = () => {
        const lang = i18n.language || 'de';
        const store = (i18n as any).store?.data?.[lang] as Record<string, any> | undefined;
        const map = new Map<string, Array<{ ns: string; key: string }>>();
        
        if (store) {
          for (const ns of Object.keys(store)) {
            const flat = flatten(store[ns]);
            for (const key of Object.keys(flat)) {
              const value = flat[key];
              if (typeof value === 'string' && value.trim().length >= 1) {
                const trimmed = value.trim();
                const list = map.get(trimmed) || [];
                list.push({ ns, key });
                map.set(trimmed, list);

                // Also map HTML-normalized version so <Trans>-rendered
                // content (where <br /> becomes a real line break) can
                // be matched via innerText which contains \n instead.
                if (/<br\s*\/?>/i.test(trimmed)) {
                  const normalized = trimmed.replace(/<br\s*\/?>/gi, '\n');
                  const nList = map.get(normalized) || [];
                  nList.push({ ns, key });
                  map.set(normalized, nList);
                }
              }
            }
          }
        }
        setReverseMap(map);
    };

    const t = setTimeout(buildMap, 100);
    return () => clearTimeout(t);
  }, [isEditMode, canEdit, i18n.language]);

  useEffect(() => {
    // Disable listener if we are currently editing something (modal open)
    if (!isEditMode || !canEdit || !reverseMap || editTarget) {
        document.body.classList.remove('edit-mode-active');
        // Also remove styles if we pause
        const s = document.getElementById('click-to-edit-styles');
        if (s) s.remove();
        return;
    }
    
    document.body.classList.add('edit-mode-active');

    // Add styles for hover effect
    const styleId = 'click-to-edit-styles';
    if (!document.getElementById(styleId)) {
        const style = document.createElement('style');
        style.id = styleId;
        style.innerHTML = `
            .edit-highlight {
                outline: 2px dotted var(--color-primary-gradient-start) !important;
                outline-offset: 2px;
                cursor: help !important;
                position: relative;
            }
            .edit-highlight::after {
                content: "✎";
                position: absolute;
                top: -10px;
                right: -10px;
                background: var(--color-primary-gradient-start);
                color: var(--color-primary-text);
                font-size: 10px;
                width: 16px;
                height: 16px;
                display: flex;
                align-items: center;
                justify-content: center;
                border-radius: var(--radius-full);
                pointer-events: none;
                z-index: 10000;
            }
        `;
        document.head.appendChild(style);
    }

    // Handler to detect text matches
    const getTextMatch = (target: HTMLElement): string | null => {
        // Check 1: Direct text node match
        if (target.childNodes.length > 0) {
            for (const node of target.childNodes) {
                if (node.nodeType === Node.TEXT_NODE && node.nodeValue && reverseMap.has(node.nodeValue.trim())) {
                     return node.nodeValue.trim();
                }
            }
        }
        
        // Check 2: innerText match (fallback)
        const text = target.innerText?.trim();
        if (text && reverseMap.has(text)) {
            // Only if this element doesn't have too many children (avoid highlighting the whole page body)
            if (target.children.length < 3) {
                return text;
            }
        }
        return null;
    };

    const handleMouseOver = (e: MouseEvent) => {
        const target = e.target as HTMLElement;
        // Ignore if dialog is open (though we disabled effect via editTarget check already)
        if (!target || target.closest('[role="dialog"]')) return;

        const match = getTextMatch(target);
        if (match) {
            target.classList.add('edit-highlight');
            e.stopPropagation();
        }
    };

    const handleMouseOut = (e: MouseEvent) => {
        const target = e.target as HTMLElement;
        if (target) target.classList.remove('edit-highlight');
    };

    const handleClick = (e: MouseEvent) => {
        const target = e.target as HTMLElement;
        if (!target || target.closest('[role="dialog"]')) return;

        const textToMatch = getTextMatch(target);
        if (textToMatch) {
            const matches = reverseMap.get(textToMatch);
            if (matches && matches.length > 0) {
                e.preventDefault();
                e.stopPropagation();
                
                setEditTarget({
                    candidates: matches.map(m => ({
                        namespace: m.ns,
                        key: m.key,
                        currentValue: textToMatch
                    })),
                    position: { x: e.clientX, y: e.clientY }
                });
            }
        }
    };

    window.addEventListener('mouseover', handleMouseOver, true);
    window.addEventListener('mouseout', handleMouseOut, true);
    window.addEventListener('click', handleClick, true);

    return () => {
        window.removeEventListener('mouseover', handleMouseOver, true);
        window.removeEventListener('mouseout', handleMouseOut, true);
        window.removeEventListener('click', handleClick, true);
        document.body.classList.remove('edit-mode-active');
        const s = document.getElementById(styleId);
        if (s) s.remove();
    };
  }, [isEditMode, canEdit, reverseMap, setEditTarget, editTarget]);

  return null;
};
